import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText } from 'lucide-react';
import { ArchivoItem } from './ArchivoItem';
import type { ArchivoRecord } from '../types';

interface ArchivosListProps {
  archivos: ArchivoRecord[];
  isLoading: boolean;
  isDeleting: boolean;
  onDelete: (archivoId: number) => Promise<void>;
  onDownload: (archivo: ArchivoRecord) => Promise<void>;
  onSendWhatsApp?: (archivo: ArchivoRecord) => void;
  canSendWhatsApp?: boolean;
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

export const ArchivosList: React.FC<ArchivosListProps> = ({
  archivos,
  isLoading,
  isDeleting,
  onDelete,
  onDownload,
  onSendWhatsApp,
  canSendWhatsApp = false,
  title = 'Archivos',
  emptyTitle = 'No hay archivos',
  emptyDescription = 'Sube el primer archivo para este trámite',
}) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="w-10 h-10 rounded" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="w-8 h-8 rounded" />
                  <Skeleton className="w-8 h-8 rounded" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (archivos.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">{emptyTitle}</h4>
          <p className="text-gray-600">{emptyDescription}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">{title} ({archivos.length})</h4>
      {archivos.map((archivo) => (
        <ArchivoItem
          key={archivo.id}
          archivo={archivo}
          isDeleting={isDeleting}
          onDelete={onDelete}
          onDownload={onDownload}
          onSendWhatsApp={onSendWhatsApp}
          canSendWhatsApp={canSendWhatsApp}
        />
      ))}
    </div>
  );
};
